var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Draggable from "react-draggable";
import { faEllipsisH } from "@fortawesome/pro-regular-svg-icons/faEllipsisH";
import { faTimes } from "@fortawesome/pro-regular-svg-icons/faTimes";
import { faQuestionCircle as farQuestionCircle } from "@fortawesome/pro-regular-svg-icons/faQuestionCircle";
import { combineSxProps, scrollOnHoverSxProps } from "../../Utils";
const dialogTitleSxProps = (isDraggable) => ({
    cursor: isDraggable ? "move" : "inherit",
    display: "flex"
});
const paperShakeAnimationSxProps = (shouldDialogShake, dialogPosition) => ({
    "& .MuiDialog-paper": {
        m: 0,
        animation: shouldDialogShake ? "shake 0.5s infinite" : "none",
        "@keyframes shake": {
            "0%": { transform: `translate(${dialogPosition.x + 0.5}px, ${dialogPosition.y + 0.5}px) rotate(0deg)` },
            "10%": { transform: `translate(${dialogPosition.x - 0.5}px, ${dialogPosition.y - 1}px) rotate(-0.5deg)` },
            "20%": { transform: `translate(${dialogPosition.x - 1.5}px, ${dialogPosition.y}px) rotate(0.5deg)` },
            "30%": { transform: `translate(${dialogPosition.x + 1.5}px, ${dialogPosition.y + 1}px) rotate(0deg)` },
            "40%": { transform: `translate(${dialogPosition.x + 0.5}px, ${dialogPosition.y - 0.5}px) rotate(0.5deg)` },
            "50%": { transform: `translate(${dialogPosition.x - 0.5}px, ${dialogPosition.y + 1}px) rotate(-0.5deg)` },
            "60%": { transform: `translate(${dialogPosition.x - 1.5}px, ${dialogPosition.y + 0.5}px) rotate(0deg)` },
            "70%": { transform: `translate(${dialogPosition.x + 1.5}px, ${dialogPosition.y + 0.5}px) rotate(-0.5deg)` },
            "80%": { transform: `translate(${dialogPosition.x - 0.5}px, ${dialogPosition.y - 0.5}px) rotate(0.5deg)` },
            "90%": { transform: `translate(${dialogPosition.x + 0.5}px, ${dialogPosition.y + 1}px) rotate(0deg)` },
            "100%": { transform: `translate(${dialogPosition.x + 0.5}px, ${dialogPosition.y - 1}px) rotate(-0.5deg)` }
        }
    }
});
const dialogContentSxProps = Object.assign(Object.assign({}, scrollOnHoverSxProps), { pl: "32px", pr: "18px", overflow: "auto" });
const iconButtonCommonSxProps = {
    width: "32px",
    height: "32px",
    fontSize: "18px"
};
const infoIconButtonSxProps = Object.assign({}, iconButtonCommonSxProps);
const closeIconButtonSxProps = Object.assign(Object.assign({}, iconButtonCommonSxProps), { "&svg": { width: "18px", height: "18px" }, "&:not([disabled]):not(:hover)": {
        color: "action.active"
    } });
export const DraggableConfirmationDialog = (props) => {
    var _a;
    const { onClose, children, title, inEditorStyling, closeIconButton, infoIconButton, primaryButton, secondaryButton, dialogActions, isDraggable, isShakeable = true } = props, dialogProps = __rest(props, ["onClose", "children", "title", "inEditorStyling", "closeIconButton", "infoIconButton", "primaryButton", "secondaryButton", "dialogActions", "isDraggable", "isShakeable"]);
    const dialogPosition = useRef({ x: 0, y: 0 });
    const [shouldDialogShake, setShouldDialogShake] = useState(false);
    const handleClose = (e, reason) => {
        if (isDraggable) {
            // todo support shakable when not draggable
            if (reason == "backdropClick" && isShakeable) {
                setShouldDialogShake(true);
                // wait for shake animation to end
                setTimeout(() => {
                    setShouldDialogShake(false);
                }, 500);
            }
            else {
                // wait for close animation to end
                setTimeout(() => {
                    dialogPosition.current = { x: 0, y: 0 };
                }, 100);
            }
        }
        onClose && onClose(e, reason);
    };
    const getPaperComponent = () => {
        if (isDraggable) {
            return createPaperComponent((event, data) => {
                dialogPosition.current = { x: data.x, y: data.y };
            });
        }
        else if (props.PaperComponent) {
            return props.PaperComponent;
        }
        return undefined;
    };
    const memoPaperComponent = useMemo(getPaperComponent, [isDraggable, props.PaperComponent]);
    return (_jsxs(Dialog, Object.assign({}, dialogProps, { onClose: handleClose, PaperComponent: memoPaperComponent, PaperProps: Object.assign(Object.assign({}, props.PaperProps), { sx: combineSxProps({ position: "relative" }, (_a = props.PaperProps) === null || _a === void 0 ? void 0 : _a.sx) }), "aria-labelledby": "draggable-dialog-title", sx: combineSxProps(props.sx, isDraggable
            ? paperShakeAnimationSxProps(shouldDialogShake, dialogPosition.current)
            : {}), title: "", children: [_jsxs(DialogTitle, { sx: dialogTitleSxProps(isDraggable), id: "draggable-dialog-title", children: [isDraggable ?
                        _jsx(Box, { sx: {
                                position: "absolute",
                                top: "4px",
                                left: "calc(50% - 8px)",
                                width: "16px",
                                height: "16px",
                                color: (theme) => theme.palette.text.disabled
                            }, children: _jsx(FontAwesomeIcon, { icon: faEllipsisH }) })
                        : _jsx(_Fragment, {}), title, _jsxs(Box, { sx: { position: "absolute", right: "6px", top: "12px" }, children: [(infoIconButton === null || infoIconButton === void 0 ? void 0 : infoIconButton.show) ?
                                _jsx(IconButton, { onClick: infoIconButton.onClick, "data-testid": infoIconButton.dataTestId, "data-tracking-id": infoIconButton.dataTrackingId, sx: infoIconButtonSxProps, children: _jsx(FontAwesomeIcon, { icon: farQuestionCircle }) })
                                : _jsx(_Fragment, {}), (closeIconButton === null || closeIconButton === void 0 ? void 0 : closeIconButton.show) ?
                                _jsx(IconButton, { color: "primary", onClick: (e) => onClose(e, "escapeKeyDown"), "data-testid": closeIconButton.dataTestId, "data-tracking-id": closeIconButton.dataTrackingId, sx: closeIconButtonSxProps, children: _jsx(FontAwesomeIcon, { icon: faTimes }) })
                                : _jsx(_Fragment, {})] })] }), _jsx(DialogContent, { sx: dialogContentSxProps, children: children }), dialogActions ? dialogActions : _jsx(_Fragment, {}), primaryButton || secondaryButton ?
                _jsxs(DialogActions, { sx: { pr: "32px" }, children: [(secondaryButton === null || secondaryButton === void 0 ? void 0 : secondaryButton.show) ?
                            _jsx(Button, { variant: inEditorStyling ? "text" : "outlined", onClick: (e) => onClose(e, "escapeKeyDown"), size: "large", "data-testid": secondaryButton.dataTestId, "data-tracking-id": secondaryButton.dataTrackingId, children: secondaryButton.text })
                            : _jsx(_Fragment, {}), (primaryButton === null || primaryButton === void 0 ? void 0 : primaryButton.show) ?
                            _jsx(Button, { variant: "contained", onClick: primaryButton.onClick, color: "primary", size: "large", "data-testid": primaryButton.dataTestId, "data-tracking-id": primaryButton.dataTrackingId, disabled: primaryButton.disabled, children: primaryButton.text })
                            : _jsx(_Fragment, {})] })
                : _jsx(_Fragment, {})] })));
};
const createPaperComponent = (onStop) => {
    return (props) => {
        return (_jsx(Draggable, { handle: "#draggable-dialog-title", cancel: "[class*=\"MuiDialogContent-root\"]", bounds: "parent", onStop: onStop, children: _jsx(Paper, Object.assign({}, props, { id: "draggable-dialog-paper" })) }));
    };
};
export default DraggableConfirmationDialog;
//# sourceMappingURL=DraggableConfirmationDialog.js.map